'use client';

import { useCallback, useMemo, useState } from 'react';

/**
 * **What the REVIEWER changed while reviewing** (VALE-1264).
 *
 * The AI's diff describes the extract as it arrived and nothing teaches it
 * about an edit made afterwards, so the treatment answers every question about
 * a flag from the row's draft alone. That is fine until the reviewer turns a
 * flag off: `Beneficiary — No` is indistinguishable from a flag nobody ever
 * set, and both altitudes of the rule read it that way at once. Answering
 * `Beneficiary` **No** on a created contact therefore deleted the row — the
 * reviewer's own correction removed the only thing they could confirm it with,
 * and left no record that the field had been touched.
 *
 * This is the missing fact, and it is one set rather than two so the two
 * altitudes cannot disagree: the review list's selection filter and the
 * treatment's marking rule both read it.
 *
 * **It is the host's, not the block's.** A contact is edited on two surfaces —
 * the detail block under its row, and the drawer opened from it — and both
 * commit through the one `onCommitFields` this hook wraps. Recording it beside
 * that call is what makes a flag turned off in the drawer keep its row in the
 * list, which recording it inside either component could not.
 *
 * Sibling of {@link useReviewRejections}, and the same keying: the row's
 * synthetic id (`pendingIdForIndex`), which the list, the drawer and the detail
 * block all use already.
 */
export interface ReviewFieldEditsApi {
  /**
   * Note that a commit was written — call it with the patch, in the SAME
   * synchronous task that initiated the commit, never after an `await`.
   *
   * The record exists so the selection filter and the marking rule can tell an
   * answered flag from one nobody set, and that only works if it is visible in
   * every render where the host's own state already holds the new value.
   * Recording after the commit resolved put the two writes a render apart, and
   * in that gap the filter dropped the answered row mid-review (VALE-1375).
   * A commit that is later refused leaves a record for a key whose value never
   * changed — the row reads as touched, which is the smaller lie than deleting
   * a row the reviewer is looking at.
   */
  record: (itemId: number, patch: object, before?: object) => void;
  /** The keys edited on one item, in the shape the treatment takes. */
  keysFor: (itemId: number) => ReadonlySet<string>;
  /**
   * **What each edited key held BEFORE the reviewer first touched it**
   * (VALE-1617) — which, on a created item, is the value the AI supplied.
   *
   * `Use suggested value` restores the AI's figure by reading the diff's
   * `proposed`, and a CREATED item has no `FieldChange` to read: the extract is
   * the proposal. So once the reviewer typed over it, nothing anywhere
   * remembered what the AI had said, and taking a reject back reinstated the
   * reviewer's own edit under a button offering the opposite. Most of an
   * extract is creates, so that was the commonest path.
   *
   * **The FIRST prior only, never the last.** A second edit's prior is the
   * reviewer's own first edit, and restoring that would hand back a value the
   * AI never proposed — the same lie in a smaller disguise.
   */
  priorsFor: (itemId: number) => ReadonlyMap<string, unknown>;
  /**
   * **Shift every key above a removed row down one** (VALE-1617) — the twin of
   * `ReviewRejectionsApi.remapAfterRemoval`, and needed for the same reason.
   *
   * Keys here are `pendingIdForIndex`, which encodes position, so deleting a
   * row leaves every later row reading the edits of the row that used to sit
   * above it. On a flag that is not cosmetic: `isUnsetFlag` and rule 10 both
   * answer from this set, so an inherited entry makes a control render as
   * though it had been answered when it had not.
   */
  remapAfterRemoval: (itemId: number) => void;
}

/**
 * Every key a patch touches, in the vocabulary the review's stores speak:
 * the top-level key, plus one level of dot-path leaves for a plain-object
 * value (VALE-1375).
 *
 * The rejections store and the diff both key nested inventory fields by
 * dot-path (`itemInfo.bsb`), while a commit's patch arrives nested
 * (`{ itemInfo: { bsb } }`). Matching either vocabulary against the other is
 * this one function's job; a host that re-derived it would be the second
 * definition this ticket exists to remove. Arrays, dates and class instances
 * are one value, exactly as `withoutRejectedFields` treats them.
 */
export function keysTouchedByPatch(patch: object): string[] {
  const keys: string[] = [];
  for (const [key, value] of Object.entries(patch)) {
    keys.push(key);
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.getPrototypeOf(value) === Object.prototype
    ) {
      for (const leaf of Object.keys(value)) keys.push(`${key}.${leaf}`);
    }
  }
  return keys;
}

/** Nothing edited — a stable identity, so `keysFor` does not thrash memos. */
const NO_KEYS: ReadonlySet<string> = new Set();
/** The same, for the priors — a stable empty identity per the reason above. */
const NO_PRIORS: ReadonlyMap<string, unknown> = new Map();

/**
 * One dot-path's value on a record, in the vocabulary
 * {@link keysTouchedByPatch} speaks.
 *
 * The parent key is walked as well as its leaves, because that function emits
 * both and a caller asking for `itemInfo` means the object.
 */
function valueAt(record: object, key: string): unknown {
  let cursor: unknown = record;
  for (const part of key.split('.')) {
    if (cursor == null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

export function useReviewFieldEdits(): ReviewFieldEditsApi {
  const [edits, setEdits] = useState<Record<number, ReadonlySet<string>>>({});
  const [priors, setPriors] = useState<
    Record<number, ReadonlyMap<string, unknown>>
  >({});

  const record = useCallback(
    (itemId: number, patch: object, before?: object) => {
      const keys = keysTouchedByPatch(patch);
      if (keys.length === 0) return;
      setEdits((current) => {
        const held = current[itemId];
        /*
          Re-editing a key already recorded must not produce a new Set: the
          treatment's mapper depends on this identity, so a fresh one on every
          keystroke-commit would rebuild every row's popover and drop an open one.
        */
        if (held && keys.every((key) => held.has(key))) return current;
        const next = new Set(held ?? []);
        for (const key of keys) next.add(key);
        return { ...current, [itemId]: next };
      });

      /*
        **Only the keys not already remembered, and only when the caller handed
        over the pre-commit record** (VALE-1617). A host that passes no `before`
        keeps exactly today's behaviour, which is what makes this additive.

        The same identity rule as the Set above: nothing new when there is
        nothing new to remember.
      */
      if (!before) return;
      setPriors((current) => {
        const held = current[itemId];
        const unseen = keys.filter((key) => !held?.has(key));
        if (unseen.length === 0) return current;
        const next = new Map(held ?? []);
        for (const key of unseen) next.set(key, valueAt(before, key));
        return { ...current, [itemId]: next };
      });
    },
    []
  );

  const keysFor = useCallback(
    (itemId: number) => edits[itemId] ?? NO_KEYS,
    [edits]
  );

  const priorsFor = useCallback(
    (itemId: number) => priors[itemId] ?? NO_PRIORS,
    [priors]
  );

  /**
   * See the API docblock. Ids are `-(index + 1)`, so a LOWER id is a LATER row
   * and every key strictly below the removed one moves a step toward zero. The
   * removed row's own entry is dropped — nothing is left for it to describe,
   * and leaving it would be inherited by whoever next lands on that id.
   */
  const remapAfterRemoval = useCallback((removedId: number) => {
    const shift = <T>(map: Record<number, T>): Record<number, T> => {
      const next: Record<number, T> = {};
      for (const [raw, value] of Object.entries(map)) {
        const id = Number(raw);
        if (id === removedId) continue;
        next[id < removedId ? id + 1 : id] = value;
      }
      return next;
    };
    setEdits(shift);
    setPriors(shift);
  }, []);

  return useMemo(
    () => ({ record, keysFor, priorsFor, remapAfterRemoval }),
    [record, keysFor, priorsFor, remapAfterRemoval]
  );
}
